import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export function useBookmarks() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: bookmarkedIds = new Set<string>(), isLoading } = useQuery({
    queryKey: ["bookmarks", user?.id],
    queryFn: async () => {
      if (!user) return new Set<string>();
      const { data, error } = await supabase
        .from("bookmarks")
        .select("article_id")
        .eq("user_id", user.id);

      if (error) throw error;
      return new Set((data || []).map((r) => r.article_id));
    },
    enabled: !!user,
  });

  const toggleMutation = useMutation({
    mutationFn: async (articleId: string) => {
      if (!user) return null;
      const isUuid =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          articleId,
        );
      if (!isUuid) return null;

      // Read the current set from the cache rather than the render-time closure
      // so two rapid toggles (before invalidation resolves) can't both observe
      // the same stale state and send the wrong insert/delete operation.
      const current =
        queryClient.getQueryData<Set<string>>(["bookmarks", user.id]) ??
        bookmarkedIds;
      const exists = current.has(articleId);
      if (exists) {
        const { error } = await supabase
          .from("bookmarks")
          .delete()
          .eq("user_id", user.id)
          .eq("article_id", articleId);
        if (error) throw error;
        return false;
      } else {
        const { error } = await supabase
          .from("bookmarks")
          .insert({ user_id: user.id, article_id: articleId });
        if (error) throw error;
        return true;
      }
    },
    onSuccess: (wasAdded) => {
      queryClient.invalidateQueries({ queryKey: ["bookmarks", user?.id] });
      if (wasAdded === true) toast.success("Article saved to bookmarks");
      else if (wasAdded === false)
        toast.success("Article removed from bookmarks");
    },
    onError: (error: unknown) => {
      toast.error("Failed to update bookmarks", {
        description: (error as Error).message,
      });
    },
  });

  return {
    bookmarkedIds,
    loading: isLoading || toggleMutation.isPending,
    toggle: toggleMutation.mutateAsync,
    refresh: () =>
      queryClient.invalidateQueries({ queryKey: ["bookmarks", user?.id] }),
    isSignedIn: !!user,
  };
}
